"use client";

import { Children, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";
import { useOutsideClick } from "@/lib/use-outside-click";

/* ── Root ───────────────────────────────────────────────────── */

interface DropdownMenuProps {
  children: React.ReactNode;
  className?: string;
}

function DropdownMenu({ children, className }: DropdownMenuProps) {
  return (
    <div className={cn("relative inline-block", className)}>{children}</div>
  );
}

/* ── Trigger ────────────────────────────────────────────────── */

interface DropdownTriggerProps extends React.ComponentProps<"button"> {
  open?: boolean;
}

function DropdownTrigger({
  className,
  open,
  children,
  ...props
}: DropdownTriggerProps) {
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn("inline-flex items-center", className)}
      {...props}
    >
      {children}
    </button>
  );
}

/* ── Content (the dropdown panel) ───────────────────────────── */

/*
  `motion.div` redefines a handful of native HTML event handlers (onDrag,
  onAnimationStart, etc.) with stricter signatures than the React DOM
  ones. We omit them from our public prop type so callers passing only
  className / id / data-attrs continue to work — none of the dropdown
  call sites in this codebase use raw HTML drag handlers.
*/
interface DropdownContentProps
  extends Omit<
    React.ComponentProps<"div">,
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
  > {
  open: boolean;
  onClose: () => void;
  align?: "start" | "end";
}

/*
  Variants drive both the panel enter/exit AND the per-child stagger via
  `staggerChildren`. Each direct child is wrapped in `motion.div` so it
  inherits the variants — that's what gives the satisfying cascade of
  items fading in one after another instead of all at once. `popLayout`
  isn't needed here because the panel collapses as a whole on close.
*/
const panelVariants = {
  open: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 480, damping: 32, staggerChildren: 0.025, delayChildren: 0.04 },
  },
  closed: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.12, ease: "easeIn" as const, staggerChildren: 0.012, staggerDirection: -1 },
  },
};
const itemVariants = {
  open: { opacity: 1, y: 0, transition: { duration: 0.14, ease: "easeOut" as const } },
  closed: { opacity: 0, y: -3, transition: { duration: 0.08 } },
};

function DropdownContent({
  open,
  onClose,
  align = "end",
  className,
  children,
  ...props
}: DropdownContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, onClose, { enabled: open, escape: true });

  /*
    AnimatePresence handles the close-time unmount so we can drop the
    setTimeout(150) `mounted` hack — the previous code coupled an arbitrary
    150ms timer to whatever the CSS duration happened to be, which silently
    desynced any time someone tweaked timing tokens. Motion's exit-complete
    callback removes the node only after the variant transition lands.
  */
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          ref={ref}
          role="menu"
          variants={panelVariants}
          initial="closed"
          animate="open"
          exit="closed"
          className={cn(
            "absolute top-full z-[var(--z-dropdown)] mt-[var(--space-1)]",
            "min-w-[12rem] overflow-hidden",
            "rounded-[var(--radius-lg)] border border-[var(--color-border-default)]",
            "bg-[var(--color-bg-elevated)] shadow-[var(--shadow-lg)]",
            "py-[var(--space-1)]",
            align === "end" && "right-0 origin-top-right",
            align === "start" && "left-0 origin-top-left",
            className
          )}
          {...props}
        >
          {Children.map(children, (child, i) => (
            // Each child gets its own variants so the parent's
            // `staggerChildren` fires per-item. The wrapper is
            // structurally transparent — items continue to be the
            // hover/click target via their own classes.
            <motion.div key={i} variants={itemVariants}>
              {child}
            </motion.div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── Item ───────────────────────────────────────────────────── */

interface DropdownItemProps extends React.ComponentProps<"button"> {
  destructive?: boolean;
}

function DropdownItem({
  className,
  destructive,
  ...props
}: DropdownItemProps) {
  return (
    <button
      role="menuitem"
      className={cn(
        "flex w-full items-center gap-[var(--space-3)]",
        "px-[var(--space-3)] py-[var(--space-2)]",
        "text-left text-[length:var(--font-size-sm)]",
        "transition-colors duration-[var(--duration-fast)]",
        "outline-none",
        "focus-visible:bg-[var(--color-bg-muted)]",
        destructive
          ? "text-[var(--color-error)] hover:bg-[var(--color-error-subtle)]"
          : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-muted)]",
        "disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
}

/* ── Separator ──────────────────────────────────────────────── */

function DropdownSeparator({ className }: { className?: string }) {
  return (
    <div
      role="separator"
      className={cn(
        "my-[var(--space-1)] h-px bg-[var(--color-border-subtle)]",
        className
      )}
    />
  );
}

/* ── Label (non-interactive) ────────────────────────────────── */

function DropdownLabel({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "px-[var(--space-3)] py-[var(--space-1-5)]",
        "text-[length:var(--font-size-xs)] font-[number:var(--font-weight-semibold)]",
        "text-[var(--color-text-muted)]",
        className
      )}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownLabel,
};
export type { DropdownMenuProps, DropdownContentProps, DropdownItemProps };
